import { createContext, useReducer } from 'react';

type RecentDataAction =
  | { type: 'SET_PROJECTS'; payload: BB.Project[] }
  | { type: 'ADD_PROJECT'; payload: BB.Project }
  | { type: 'SET_REPOSITORIES'; payload: BB.Repository[] }
  | { type: 'SET_PROJECTS_LOADING'; payload: boolean }
  | { type: 'SET_REPOS_LOADING'; payload: boolean };

type RecentDataContextType = {
  recentProjects: BB.Project[];
  recentRepositories: BB.Repository[];
  isProjectsLoading: boolean;
  isRepositoriesLoading: boolean;
};

export const RecentDataContext = createContext<RecentDataContextType>({
  recentProjects: [],
  recentRepositories: [],
  isProjectsLoading: false,
  isRepositoriesLoading: false,
});

export const RecentDataDispatchContext = createContext<
  React.Dispatch<RecentDataAction>
>(
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  () => {}
);

type ProviderProps = {
  children: React.ReactNode;
};

const addProjectToState = (
  state: RecentDataContextType,
  project: BB.Project
) => {
  const index = state.recentProjects.findIndex(p => p.uuid === project.uuid);
  if (index === -1) {
    // Project not found, add it to the beginning
    return {
      ...state,
      recentProjects: [project, ...state.recentProjects],
    };
  }
  if (index === 0) {
    // Project is already at the beginning, no need to update
    return state;
  }
  // Project found, move it to the beginning
  const updatedProjects = [...state.recentProjects];
  updatedProjects.splice(index, 1); // Remove the project from its current position
  updatedProjects.unshift(project); // Add it to the beginning
  return {
    ...state,
    recentProjects: updatedProjects,
  };
};

function reducer(
  state: RecentDataContextType,
  action: RecentDataAction
): RecentDataContextType {
  switch (action.type) {
    case 'SET_PROJECTS':
      return { ...state, recentProjects: action.payload };
    case 'ADD_PROJECT':
      return addProjectToState(state, action.payload);
    case 'SET_REPOSITORIES':
      return { ...state, recentRepositories: action.payload };
    case 'SET_PROJECTS_LOADING':
      return { ...state, isProjectsLoading: action.payload };
    case 'SET_REPOS_LOADING':
      return { ...state, isRepositoriesLoading: action.payload };
    default:
      return state;
  }
}

const initialState: RecentDataContextType = {
  recentProjects: [],
  recentRepositories: [],
  isProjectsLoading: false,
  isRepositoriesLoading: false,
};

export const RecentDataProvider = ({ children }: ProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <RecentDataContext.Provider value={state}>
      <RecentDataDispatchContext.Provider value={dispatch}>
        {children}
      </RecentDataDispatchContext.Provider>
    </RecentDataContext.Provider>
  );
};
